import sdk from '@/client/api/sdk';
import { CODE_SUCCESS } from '@/constants/api';

// ============================================================================
// 统一类型定义
// ============================================================================

// 统一的API响应类型
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
}

// 管理员信息接口
export interface AdminInfo {
  adminId: number;
  adminName: string;
}

// 积分赠送参数接口
export interface GrantIntegralParams {
  userName: string;
  points: number;
  remark?: string;
}

// 设置验证码参数接口
export interface SetCodeParams {
  loginType: 'email' | 'phone';
  value: string;
  code: string;
}

// 设置图片计数参数接口
export interface SetImageCountParams {
  imageId: string;
  likeCount: number;
  saveCount: number;
}

// 设置工作流计数参数接口
export interface SetWorkflowCountParams {
  workflowId: string;
  likeCount: number;
  saveCount: number;
  runCount: number;
}

// 工作流相关类型
export interface WorkflowSubmitData {
  workflowId?: string;
  workSpaceId?: string;
  outputType?: string;
  basePrice?: number | any[];
  baseTime?: number | any[];
  autoPublish?: boolean;
  [key: string]: any;
}

// 工作空间相关类型
export interface WorkspaceListResponse {
  code: string;
  data: string[];
}

export interface WorkspaceOption {
  id: string;
  label: string;
}

// 工作流节点相关类型
export interface WorkflowNodes {
  workflowId: string;
  nodes: string[];
}

export interface WorkflowNodesResponse {
  code: string;
  data: WorkflowNodes[];
}

// GPU族群相关类型
export interface GpuFamilyItem {
  id: number;
  name: string;
}

export interface GpuFamilyListResponse {
  code: string;
  data: GpuFamilyItem[];
}

// ============================================================================
// 统一工具函数
// ============================================================================

// 统一错误处理函数
const handleApiError = (error: unknown, context?: string): ApiResponse => {
  console.error(`API Error${context ? ` in ${context}` : ''}:`, error);

  if (error instanceof Error) {
    if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch')) {
      return {
        success: false,
        message: '网络连接错误，请检查您的网络连接',
      };
    }
    return {
      success: false,
      message: error.message,
    };
  }

  return {
    success: false,
    message: '发生未知错误',
  };
};

// 检查响应是否成功
const isSuccessResponse = (response: any): boolean => {
  return response.data?.code === CODE_SUCCESS;
};

// ============================================================================
// 认证相关API
// ============================================================================

/**
 * 管理员登录
 */
export async function adminLogin(
  name: string,
  password: string,
): Promise<{ success: boolean; data?: any; message?: string }> {
  try {
    const response = await sdk.POST('/api/admin/login', {
      body: { name, password },
    });

    if (response.data?.code === CODE_SUCCESS) {
      const { token, adminName, adminId } = response.data.data;

      localStorage.setItem('adminToken', token);
      localStorage.setItem('adminName', adminName);
      localStorage.setItem('adminId', adminId.toString());

      return { success: true, data: { token, adminName, adminId } };
    }

    console.warn('Admin login failed:', response.data?.code || 'Unknown error');
    return { success: false, message: response.data?.code || 'Login failed' };
  } catch (error) {
    console.error('Admin login error:', error);
    return { success: false, message: 'Network error or server unavailable' };
  }
}

/**
 * 获取当前管理员信息
 */
export async function getCurrentAdmin(): Promise<AdminInfo | null> {
  try {
    const response = await sdk.GET('/api/admin/current');

    if (response.data?.data) {
      return response.data.data;
    }

    return null;
  } catch (error) {
    console.error('Get current admin error:', error);
    return null;
  }
}

/**
 * 修改密码
 */
export async function changeAdminPassword(oldPassword: string, newPassword: string): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/changePassword', {
      body: { oldPassword, newPassword },
    });

    if (isSuccessResponse(response)) {
      return { success: true };
    }

    return { success: false, message: response.data?.code };
  } catch (error) {
    return handleApiError(error, 'changePassword');
  }
}

// ============================================================================
// 工作空间相关API
// ============================================================================

/**
 * 获取工作空间列表
 */
export async function getWorkspaceList(): Promise<WorkspaceListResponse> {
  try {
    const response = await sdk.GET('/api/admin/workspaces' as any, {});

    if (response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch workspaces: No data returned');
  } catch (error) {
    console.error('Failed to fetch workspaces:', error);
    throw error;
  }
}

// ============================================================================
// GPU族群相关API
// ============================================================================

/**
 * 获取GPU族群列表
 */
export async function getGpuFamilyList(): Promise<GpuFamilyListResponse> {
  try {
    const response = await sdk.GET('/api/admin/gpufamilies' as any, {});

    if (response.data) {
      return response.data;
    }

    throw new Error('Failed to fetch GPU families: No data returned');
  } catch (error) {
    console.error('Failed to fetch GPU families:', error);
    throw error;
  }
}

// ============================================================================
// 技能管理相关API
// ============================================================================

/**
 * 积分赠送
 */
export async function grantIntegral(params: GrantIntegralParams): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/permanent/grant', {
      body: {
        userName: params.userName,
        points: params.points,
        remark: params.remark,
      },
    });

    if (isSuccessResponse(response) && response.data?.data) {
      return {
        success: true,
        message: `成功向用户 ${params.userName} 赠送了 ${params.points} 积分`,
        data: response.data.data,
      };
    }

    return { success: false, message: response.data?.code };
  } catch (error) {
    return handleApiError(error, 'grantIntegral');
  }
}

/**
 * 设置验证码
 */
export async function setCode(params: SetCodeParams): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/setcode', {
      body: params,
    });

    if (isSuccessResponse(response)) {
      return { success: true, message: '验证码设置成功' };
    }

    return { success: false, message: response.data?.code };
  } catch (error) {
    return handleApiError(error, 'setCode');
  }
}

/**
 * 设置图片计数
 */
export async function setImageCount(params: SetImageCountParams): Promise<ApiResponse> {
  try {
    const response = await (sdk as any).POST('/api/admin/image/setcount', {
      body: params,
    });

    if (isSuccessResponse(response)) {
      return { success: true, message: '图片计数设置成功' };
    }

    return { success: false, message: response.data?.code };
  } catch (error) {
    return handleApiError(error, 'setImageCount');
  }
}

/**
 * 设置工作流计数
 */
export async function setWorkflowCount(params: SetWorkflowCountParams): Promise<ApiResponse> {
  try {
    const response = await (sdk as any).POST('/api/admin/workflow/setcount', {
      body: params,
    });

    if (isSuccessResponse(response)) {
      return { success: true, message: '工作流计数设置成功' };
    }

    return { success: false, message: response.data?.code };
  } catch (error) {
    return handleApiError(error, 'setWorkflowCount');
  }
}

// ============================================================================
// 工作流相关API
// ============================================================================

/**
 * 获取工作流列表
 */
export async function getAdminWorkflowList(): Promise<{ success: boolean; data?: any[] }> {
  try {
    const response = await sdk.GET('/api/admin/workflow/list' as any, {});

    if (response.data?.code === CODE_SUCCESS) {
      return { success: true, data: response.data.data || [] };
    }

    return { success: false };
  } catch (error) {
    console.error('Get workflow list error:', error);
    return { success: false };
  }
}

/**
 * 获取工作流详情
 */
export async function getWorkflowDetail(workflowId: string): Promise<{ success: boolean; data?: any }> {
  try {
    const response = await sdk.GET('/api/workflow', {
      params: { query: { workflowId } },
    });

    if (response.data?.code === CODE_SUCCESS) {
      return { success: true, data: response.data.data };
    }

    return { success: false };
  } catch (error) {
    console.error('Get workflow detail error:', error);
    return { success: false };
  }
}

/**
 * 获取图片详情
 */
export async function getImageDetail(imageId: string): Promise<{ success: boolean; data?: any }> {
  try {
    const response = await sdk.GET('/api/image', {
      params: { query: { imageId } },
    });

    if (response.data?.code === CODE_SUCCESS) {
      return { success: true, data: response.data.data };
    }

    return { success: false };
  } catch (error) {
    console.error('Get image detail error:', error);
    return { success: false };
  }
}

/**
 * 提交工作流配置
 */
export async function submitWorkflow(data: WorkflowSubmitData): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/workflow', {
      body: data as any,
    });

    if (response.data?.code === CODE_SUCCESS) {
      return {
        success: true,
        message: data.workflowId ? '工作流更新成功' : '工作流创建成功',
        data: response.data.data,
      };
    }

    return { success: false, message: `${response.error?.['data']}` };
  } catch (error) {
    return handleApiError(error, 'submitWorkflow');
  }
}

/**
 * 设置默认工作流
 */
export async function setDefaultWorkflow(workflowId: string): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/workflow/default', {
      params: { query: { workflowId } },
    });

    if (isSuccessResponse(response)) {
      return { success: true, message: '已设置为默认工作流' };
    }

    return { success: false };
  } catch (error) {
    return handleApiError(error, 'setDefaultWorkflow');
  }
}

/**
 * 上传工作流封面
 */
export async function uploadWorkflowCover(file: File): Promise<ApiResponse<{ url: string }>> {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await sdk.POST('/api/admin/workflow/cover', {
      body: formData as any,
    });

    if (isSuccessResponse(response) && response.data?.data?.url) {
      return {
        success: true,
        message: '封面上传成功',
        data: { url: response.data.data.url },
      };
    }

    return { success: false, message: '封面上传失败' };
  } catch (error) {
    return handleApiError(error, 'uploadCover');
  }
}

/**
 * 获取工作流的算力节点
 */
export async function getWorkflowNodes(): Promise<WorkflowNodesResponse> {
  try {
    const response = await sdk.GET('/api/admin/workflow/nodes' as any, {});

    if (response.data) {
      return response.data as WorkflowNodesResponse;
    }

    throw new Error('Failed to fetch workflow nodes: No data returned');
  } catch (error) {
    console.error('Failed to fetch workflow nodes:', error);
    throw error;
  }
}

// ============================================================================
// 节点管理相关API
// ============================================================================

// 节点信息接口
export interface NodeInfo {
  systemUUID: string;
  userID: string;
  nickName: string;
  email: string;
  deviceStatus: string;
  onlineStatus: string;
  cpu: string;
  gpu: string;
  memory: number;
  os: string;
  ip: string;
  failureRate: number;
  imageCountTotal: number;
  imageCount30Days: number;
  onlineSecondsTotal: number;
  registerTime: number;
  isBan: boolean;
  phone: string;
  noticeEn: string;
  noticeZh: string;
  clientTasks?: ClientTask[] | null;
  extraJson?: string | null;
  diskInfo?: string | null;
  remark?: string;
}

// 客户端任务相关类型
export interface ClientTask {
  batchId: string;
  taskType: string;
  createTime: number;
  taskList: {
    task: string;
    status: 'finished' | 'unfinished' | 'error';
    errorMessage: string | null;
  }[];
}

// 提交客户端任务参数
export interface SubmitClientTaskParams {
  uuid: string;
  taskType: string;
  taskList: string[];
}

// 提交客户端任务响应
export interface SubmitClientTaskResponse {
  batchID: string;
}

export interface NodeListResponse {
  code: string;
  data: {
    total: number;
    pageIndex: number;
    pageSize: number;
    pageData: NodeInfo[];
  };
}

export interface UserExtra {
  userId: string;
  userName: string;
  avatar: string;
  publishedCount: number;
  likedCount: number;
  savedCount: number;
  phone: string;
  email: string;
  registerTime: number;
  memberType: string;
  status: string;
  temporaryPoints: number;
  permanentPoints: number;
  trackingID?: string;
  from?: string;
  clientIP?: string;
}

export interface UserListResponse {
  code: string;
  data: {
    total: number;
    pageIndex: number;
    pageSize: number;
    pageData: UserExtra[];
  };
}

/** Cloud 用户列表项（与 OpenAPI CloudUserListItem 一致） */
export interface CloudUserInfo {
  userId: string;
  userName: string;
  avatar: string;
  email: string;
  phone: string;
}

export interface CloudUserListItem {
  totalConsume: number;
  currentConsuming: number;
  remark: string;
  consoleToken: string;
  balance: number;
  userInfo: CloudUserInfo;
}

export interface CloudUserListResponse {
  code: string;
  data: {
    total: number;
    pageIndex: number;
    pageSize: number;
    pageData: CloudUserListItem[];
  };
}

/**
 * 获取节点列表
 */
export async function getNodeList(
  pageIndex: number = 0,
  pageSize: number = 25,
  status: 'all' | 'normal' | 'banned' | 'online' = 'all',
): Promise<{ success: boolean; data?: NodeListResponse['data'] }> {
  try {
    const response = await sdk.GET('/api/admin/node/list' as any, {
      params: {
        query: {
          pageIndex: pageIndex + 1, // API 使用 1-based 页码
          pageSize,
          ...(status !== 'all' && { status }),
        },
      },
    });

    if (response.data?.code === CODE_SUCCESS && response.data?.data) {
      return { success: true, data: response.data.data };
    }

    return { success: false };
  } catch (error) {
    console.error('Get node list error:', error);
    return { success: false };
  }
}

/**
 * 更新节点状态（封禁/解封）
 */
export async function updateNodeStatus(
  uuids: string[],
  status: 'banned' | 'unbanned',
  noticeEN?: string,
  noticeZh?: string,
): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/node/status' as any, {
      body: {
        uuids,
        status,
        noticeEN: noticeEN || '',
        noticeZh: noticeZh || '',
      },
    });

    if (response.data?.code === CODE_SUCCESS) {
      return {
        success: true,
        message: status === 'banned' ? '节点封禁成功' : '节点解封成功',
      };
    }

    return { success: false, message: response.data?.code };
  } catch (error) {
    return handleApiError(error, 'updateNodeStatus');
  }
}

/**
 * 更新节点备注
 */
export async function updateNodeRemark(systemUUID: string, remark: string): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/node/remark' as any, {
      body: {
        systemUUID,
        remark,
      },
    });

    if (isSuccessResponse(response)) {
      return {
        success: true,
        message: '备注更新成功',
      };
    }

    return { success: false, message: response.data?.code || '备注更新失败' };
  } catch (error) {
    return handleApiError(error, 'updateNodeRemark');
  }
}

/**
 * 获取用户列表
 */
export async function getUserList(
  pageIndex: number = 0,
  pageSize: number = 25,
): Promise<{ success: boolean; data?: UserListResponse['data'] }> {
  try {
    const response = await sdk.GET('/api/admin/user/list' as any, {
      params: {
        query: {
          pageIndex: pageIndex + 1, // API 使用 1-based 页码
          pageSize,
        },
      },
    });

    if (response.data?.code === CODE_SUCCESS && response.data?.data) {
      return { success: true, data: response.data.data };
    }

    return { success: false };
  } catch (error) {
    console.error('Get user list error:', error);
    return { success: false };
  }
}

/**
 * 获取 Cloud 用户列表
 */
export async function getCloudUserList(
  pageIndex: number = 0,
  pageSize: number = 25,
  consumingFilter: 'all' | 'consuming' = 'all',
): Promise<{ success: boolean; data?: CloudUserListResponse['data'] }> {
  try {
    const response = await sdk.GET('/api/admin/cloud/user/list' as any, {
      params: {
        query: {
          pageIndex: pageIndex + 1,
          pageSize,
          ...(consumingFilter === 'consuming' ? { consumingFilter: 'consuming' } : {}),
        },
      },
    });

    if (response.data?.code === CODE_SUCCESS && response.data?.data) {
      return { success: true, data: response.data.data };
    }

    return { success: false };
  } catch (error) {
    console.error('Get cloud user list error:', error);
    return { success: false };
  }
}

/**
 * 更新 Cloud 用户备注
 */
export async function updateCloudUserRemark(userId: number, remark: string): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/cloud/user/remark' as any, {
      body: {
        userId,
        remark,
      },
    });

    if (isSuccessResponse(response)) {
      return {
        success: true,
        message: '备注更新成功',
      };
    }

    return { success: false, message: response.data?.code || '备注更新失败' };
  } catch (error) {
    return handleApiError(error, 'updateCloudUserRemark');
  }
}

/** Cloud 余额调整（赠送 / 扣除），对应 /api/admin/cloud/balance/adjust；userName 可为昵称或用户 ID 字符串，由服务端解析 */
export interface AdjustCloudBalanceParams {
  userName: string;
  action: 'add' | 'subtract';
  value: number;
  remark?: string;
}

/**
 * 调整 Cloud 用户余额
 */
export async function adjustCloudBalance(params: AdjustCloudBalanceParams): Promise<ApiResponse> {
  try {
    const response = await sdk.POST('/api/admin/cloud/balance/adjust' as any, {
      body: {
        userName: params.userName.trim(),
        action: params.action,
        value: params.value,
        remark: params.remark ?? '',
      },
    });

    if (isSuccessResponse(response)) {
      const verb = params.action === 'add' ? '赠送' : '扣除';
      return {
        success: true,
        message: `已成功${verb}额度`,
      };
    }

    return { success: false, message: response.data?.code || '额度调整失败' };
  } catch (error) {
    return handleApiError(error, 'adjustCloudBalance');
  }
}

// ============================================================================
// 客户端任务相关API
// ============================================================================

/**
 * 提交客户端任务
 */
export async function submitClientTask(params: SubmitClientTaskParams): Promise<ApiResponse<SubmitClientTaskResponse>> {
  try {
    const response = await sdk.POST('/api/admin/client/task' as any, {
      body: {
        uuid: params.uuid,
        taskType: params.taskType,
        taskList: params.taskList,
      },
    });

    if (isSuccessResponse(response) && response.data?.data) {
      return {
        success: true,
        message: '任务提交成功',
        data: response.data.data,
      };
    }

    return { success: false, message: response.data?.code || '任务提交失败' };
  } catch (error) {
    return handleApiError(error, 'submitClientTask');
  }
}

/**
 * 通过 batchId 查询任务进度
 */
export async function getClientTaskBatch(batchId: string): Promise<ApiResponse<ClientTask>> {
  try {
    const response = await sdk.GET('/api/admin/client/task/batch' as any, {
      params: {
        query: {
          batchId,
        },
      },
    });

    if (isSuccessResponse(response) && response.data?.data) {
      return {
        success: true,
        data: response.data.data.clientTask,
      };
    }

    return { success: false, message: response.data?.code || '查询任务进度失败' };
  } catch (error) {
    return handleApiError(error, 'getClientTaskBatch');
  }
}

/**
 * 通过 uuid 获取任务列表
 */
export async function getClientTasks(uuid: string): Promise<ApiResponse<ClientTask[]>> {
  try {
    const response = await sdk.GET('/api/admin/client/tasks' as any, {
      params: {
        query: {
          uuid,
        },
      },
    });

    if (isSuccessResponse(response) && response.data?.data) {
      return {
        success: true,
        data: response.data.data.clientTasks || [],
      };
    }

    return { success: false, message: response.data?.code || '获取任务列表失败' };
  } catch (error) {
    return handleApiError(error, 'getClientTasks');
  }
}
